import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { FC } from "react";
import ModalBoxCore from "../../core-components/modal-box/ModalBoxCore";
import ModalBody from "../../core-components/modal-box/modal-body/ModalBody";
import CoreButtons from "../../core-components/button/CoreButtons";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../../types/coreButtonTypes";
import "./termsBlockingModal.css";

interface ITermsBlockingModalProps {
	open: boolean;
	/** From `canAccept`. False for a team member, who cannot bind the business. */
	canAccept: boolean;
	/** `acceptedVersion === null` — nothing has changed for them, so no key-changes list. */
	isNewVendor: boolean;
	keyChanges: string[];
	/** Rendered from the API's `cooldownExpiresAt`, never from local state. */
	isReminderOnCooldown: boolean;
	isReminding: boolean;
	reminderRecipientName: string | null;
	reminderError: string | null;
	onReviewTerms: () => void;
	onRemindSuperAdmin: () => void;
	onLogout: () => void;
}

/**
 * Shown for `status === 'blocked'`. Never rendered alongside the notice banner.
 *
 * A notification surface only: it carries no consent checkbox, because consent
 * belongs with the document the vendor consented to. The CTA opens the viewer.
 *
 * `onClose` is a no-op throughout, which is what makes ESC and backdrop clicks
 * inert — MUI routes both through it.
 */
const TermsBlockingModal: FC<ITermsBlockingModalProps> = ({
	open,
	canAccept,
	isNewVendor,
	keyChanges,
	isReminderOnCooldown,
	isReminding,
	reminderRecipientName,
	reminderError,
	onReviewTerms,
	onRemindSuperAdmin,
	onLogout,
}) => {
	const title = isNewVendor
		? "Accept our Supplier Terms & Conditions"
		: "We've updated our Supplier Terms & Conditions";

	const intro = isNewVendor
		? "Please review and accept our terms and conditions to start receiving work on YouCater."
		: "Please review and accept our updated terms and conditions to keep using YouCater.";

	// The CMS decides what belongs in keyChanges for every vendor, new or
	// returning — for a first-ever acceptance it describes what the document
	// establishes rather than a diff, so it's still meaningful here. Content,
	// not isNewVendor, is what should gate visibility.
	const showKeyChanges = keyChanges.length > 0;

	return (
		<ModalBoxCore open={open} onClose={() => {}} className="termsBlockingModal">
			<Box className="termsBlockingHeader">
				<Typography className="termsBlockingTitle">{title}</Typography>
			</Box>

			<ModalBody>
				{canAccept ? (
					<Typography className="termsBlockingIntro">{intro}</Typography>
				) : (
					<Box className="termsTeamMemberNotice">
						<Typography className="termsTeamMemberText">
							Only your <strong>Super Admin</strong> can accept these terms. Until they do, your team
							won&apos;t be able to continue on YouCater.
						</Typography>
					</Box>
				)}

				{showKeyChanges && (
					<>
						<Typography className="termsKeyChangesTitle">Key changes include:</Typography>
						<List className="termsKeyChangesList">
							{keyChanges.map((change) => (
								<ListItem key={change} className="termsKeyChangeItem">
									<ListItemIcon className="termsBulletIcon">
										<span className="termsBullet">•</span>
									</ListItemIcon>
									<ListItemText primary={change} className="termsKeyChangeText" />
								</ListItem>
							))}
						</List>
					</>
				)}

				{/* Figma 54942:34 — team members read the document; only Super Admin can accept. */}
				{!canAccept && (
					<button type="button" className="termsViewFullLink" onClick={onReviewTerms}>
						<span className="termsViewFullIcon" aria-hidden>
							👉
						</span>
						<span className="termsViewFullText">View Full Terms &amp; Conditions</span>
					</button>
				)}

				{/* Confirms in place — the surface stays open, because a blocked team
				    member has nowhere else to go. */}
				{!canAccept && reminderRecipientName && (
					<Box className="termsReminderSent">
						<Typography className="termsReminderSentText">
							✓ Reminder sent to {reminderRecipientName}
						</Typography>
						<Typography className="termsReminderSentHint">
							You can send another reminder in 24 hours.
						</Typography>
					</Box>
				)}

				{!canAccept && reminderError && (
					<Typography className="termsReminderError">{reminderError}</Typography>
				)}
			</ModalBody>

			{/*
			 * Figma btnGroup: Log out (left) + primary CTA (right). After a reminder
			 * is sent, only Log out remains — full width via CSS :only-child.
			 */}
			<Box className="termsBlockingActions">
				<CoreButtons
					buttonText="Log out"
					variant={BUTTON_VARIANT.OUTLINED}
					color={BUTTON_COLOR.INHERIT}
					coreBtnPill={true}
					className="termsLogoutButton"
					onclick={onLogout}
				/>

				{canAccept && (
					<CoreButtons
						buttonText="Review & accept terms"
						variant={BUTTON_VARIANT.CONTAINED}
						color={BUTTON_COLOR.PRIMARY}
						coreBtnPill={true}
						onclick={onReviewTerms}
					/>
				)}

				{!canAccept && !reminderRecipientName && (
					<CoreButtons
						buttonText="Remind Super Admin"
						variant={BUTTON_VARIANT.CONTAINED}
						color={BUTTON_COLOR.PRIMARY}
						coreBtnPill={true}
						disabled={isReminderOnCooldown || isReminding}
						onclick={onRemindSuperAdmin}
					/>
				)}
			</Box>
		</ModalBoxCore>
	);
};

export default TermsBlockingModal;
